import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/Button';
import { Icon, type IconName } from '@/components/ui/Icon';
import { Skeleton } from '@/components/ui/Skeleton';
import { endpoints } from '@/api';
import { ApiError, errorMessage } from '@/api/errors';
import { useConfig } from '@/api/queries';
import { useAuth } from '@/stores/auth';
import { useModeration } from '@/stores/moderation';
import { loadGoogleIdentity, renderGoogleButton } from '@/lib/google';
import { inAppBrowser } from '@/lib/inapp';
import { useAction } from '@/lib/useAction';
import { landingBeacon } from '@/lib/beacon';
import { OpenInBrowser } from '@/features/links/OpenInBrowser';

/** Friendly copy for POST /v1/auth/google failures (SPEC_V2 §1). */
export function googleAuthError(e: unknown): { message: string; closed?: 'underage' | 'account_banned' } {
  if (e instanceof ApiError) {
    if (e.code === 'underage' || e.details.reason === 'underage') return { message: 'This account is closed.', closed: 'underage' };
    if (e.code === 'account_banned') return { message: 'This Google account can’t be used on Popin. It was closed for breaking our community guidelines.', closed: 'account_banned' };
    if (e.code === 'auth_unavailable' || e.status === 503) return { message: 'Sign-in is temporarily unavailable. Try again in a few minutes.' };
    if (e.code === 'rate_limited') return { message: `Too many attempts. Try again in ${e.retryAfter ?? 60} seconds.` };
    if (e.details.reason === 'email_unverified') return { message: 'Your Google account’s email isn’t verified yet. Verify it with Google, then try again.' };
    if (e.status === 401 || e.code === 'unauthorized' || e.code === 'validation_failed') return { message: 'Google couldn’t confirm that sign-in. Please try again.' };
  }
  return { message: errorMessage(e) };
}

type GisState = 'loading' | 'ready' | 'error';

export function GoogleButton({ clientId, onCredential, disabled, oneTap = false, text, onDark = false }: { clientId: string; onCredential: (c: string) => void; disabled: boolean; oneTap?: boolean; text?: 'continue_with' | 'signup_with'; onDark?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<GisState>('loading');
  const [err, setErr] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const cb = useRef(onCredential);
  cb.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    loadGoogleIdentity()
      .then((api) => {
        if (cancelled || !ref.current) return;
        renderGoogleButton(api, ref.current, clientId, (c) => cb.current(c), { oneTap, text, theme: onDark ? 'outline' : undefined });
        setState('ready');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : 'Google sign-in couldn’t load.');
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, attempt, oneTap, text, onDark]);

  return (
    <div>
      <div className="relative min-h-[44px]">
        {state === 'loading' && <Skeleton className="absolute inset-x-0 top-0 h-11 max-w-[320px]" rounded="rounded-full" />}
        <div
          ref={ref}
          data-testid="google-signin-container"
          aria-busy={state === 'loading' || disabled || undefined}
          className={disabled ? 'pointer-events-none opacity-50' : undefined}
          style={{ colorScheme: 'normal' }}
        />
      </div>
      {state === 'error' && (
        <div role="alert" className="mt-3 rounded-card bg-surface p-4 text-[15px]" data-testid="google-load-error">
          <p className="font-medium text-danger">{err}</p>
          <Button size="sm" variant="outline" accent="paper" icon="flip" className="mt-3" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

const POINTS: { icon: IconName; text: string }[] = [
  { icon: 'heart', text: 'Free. Your first date can be minutes away.' },
  { icon: 'shield', text: '18+ only. You confirm your birthday once; it’s never shown to anyone.' },
  { icon: 'user', text: 'From Google we only use your name and email. No contacts, no passwords.' },
  { icon: 'cam-off', text: 'Video first, number later. Calls are never recorded.' },
];

/**
 * Google sign-in for the landing and login pages: one tap creates the
 * account or signs you back in. New accounts are counted as a signup in
 * Admin → Traffic (site app); PublicOnly then sends them to onboarding.
 */
export function useGoogleSignIn() {
  const setSession = useAuth((s) => s.setSession);
  const setClosed = useModeration((s) => s.setClosed);
  const [err, setErr] = useState<string | null>(null);
  const [onCredential, busy] = useAction(async (credential: string) => {
    setErr(null);
    try {
      const r = await endpoints.googleAuth(credential);
      if (!r.user.onboarded) landingBeacon('signup', 0, '/signed-up');
      setSession(r.access_token, r.user); // PublicOnly redirects (→ /onboarding for new accounts)
    } catch (e) {
      const { message, closed } = googleAuthError(e);
      if (closed === 'underage') setClosed('underage');
      setErr(message);
    }
  });
  return { onCredential, busy, err };
}

/**
 * The sign-up box: the Google button (with One Tap where Chrome offers it),
 * or, inside Instagram/Facebook/… where Google refuses, a way out to the
 * real browser. Used on the landing page and /login.
 */
export function SignUpBox({ oneTap = false, onDark = false, className }: { oneTap?: boolean; onDark?: boolean; className?: string }) {
  const config = useConfig();
  const { onCredential, busy, err } = useGoogleSignIn();
  const clientId = config.data?.google_client_id ?? null;
  if (inAppBrowser())
    return (
      <div className={className}>
        <OpenInBrowser tone={onDark ? 'bone' : 'night'} />
      </div>
    );
  return (
    <div className={className} data-testid="signup-box">
      {config.isLoading ? (
        <Skeleton className="h-11 w-full max-w-[320px]" rounded="rounded-full" />
      ) : config.isError ? (
        <div role="alert" className="rounded-card bg-surface p-5 text-fg">
          <p className="text-[17px] font-semibold">Can’t reach Popin right now.</p>
          <p className="mt-1 text-sm text-muted">{errorMessage(config.error)}</p>
          <Button size="sm" variant="outline" accent="paper" icon="flip" className="mt-4" onClick={() => config.refetch()}>
            Try again
          </Button>
        </div>
      ) : !clientId ? (
        <div className="rounded-card bg-surface p-5 text-fg" data-testid="google-not-configured">
          <p className="flex items-center gap-2 text-[17px] font-semibold">
            <Icon name="clock" size={20} /> Sign-in isn’t configured
          </p>
          <p className="mt-1 text-[15px] text-muted">Popin uses Google to sign you in, and this server hasn’t been set up for it yet. Please check back soon.</p>
          {import.meta.env.DEV && (
            <p className="mt-3 rounded-card bg-raised px-3 py-2 text-[13px] text-fg">
              Dev: set <span className="font-semibold">GOOGLE_CLIENT_ID</span> on the API server and restart it.
            </p>
          )}
        </div>
      ) : (
        <>
          <GoogleButton clientId={clientId} onCredential={(c) => void onCredential(c)} disabled={busy} oneTap={oneTap} onDark={onDark} />
          {busy && (
            <p className="mt-3 text-[15px] font-medium" role="status" data-testid="google-signing-in">
              Signing you in…
            </p>
          )}
        </>
      )}
      {err && (
        <p role="alert" className="mt-4 rounded-card bg-surface px-4 py-3 text-[15px] font-medium text-danger" data-testid="auth-error">
          {err}
        </p>
      )}
    </div>
  );
}

export function Login() {
  useEffect(() => landingBeacon('view'), []);
  return (
    <AuthLayout
      title={
        <>
          <span className="block">Pop in.</span> <span className="block">Meet someone real.</span>
        </>
      }
      sub="One tap with Google and you’re in. New here? The same button creates your account, free."
      footer={
        <>
          By continuing you confirm you’re 18 or older and agree to our{' '}
          <Link to="/safety" className="font-semibold text-fg underline underline-offset-4">
            community guidelines
          </Link>
          .
        </>
      }
    >
      <div data-testid="login-screen">
        <SignUpBox oneTap />
        <ul className="mt-10 space-y-3">
          {POINTS.map((p) => (
            <li key={p.text} className="flex gap-3 text-[15px] text-muted">
              <Icon name={p.icon} size={20} className="mt-0.5 shrink-0 text-fg" />
              {p.text}
            </li>
          ))}
        </ul>
      </div>
    </AuthLayout>
  );
}
